"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Loader2, ZapIcon } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Card, CardContent } from "@/components/ui/card"
import { MatchExplainer } from "./match-explainer"
import { useNavigate } from "@tanstack/react-router"
import { useGetInstantmatchGetcount, useGetInstantmatchResults, usePostInstantmatchTrigger } from '../../../../openapi/api/endpoints/default/default';


interface Skill {
  name: string
  score: number
}

interface Candidate {
  id: string
  name: string
  title: string
  email: string
  matchScore: number
  skills: Skill[]
  avatar?: string
}

interface InstantMatchButtonProps {
  jobTitle: string
  requiredSkills: string[]
  niceToHaveSkills: string[],
  hiringCampaignId:string
}

export function InstantMatchButton({ jobTitle, requiredSkills, niceToHaveSkills,hiringCampaignId }: InstantMatchButtonProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [matchResults, setMatchResults] = useState<Candidate[] | null>(null)
  const [open, setOpen] = useState(false)
  const {data:profiles}=useGetInstantmatchResults({hiringCampaignId})
  const [enabled, setEnabled] = useState(!!profiles)
  const navigate=useNavigate()
  const {mutateAsync:triggerInstantSearches}=usePostInstantmatchTrigger()
  const {data:potentialMatchesCount}=useGetInstantmatchGetcount({hiringCampaignId})


  //mutateAsync({data:{hiringCampaignId}})

  // This would be replaced with an actual API call to your backend
  const findMatches = async () => {
    setIsLoading(true)

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 1500))

    // Mock data - in a real app, this would come from your API
    const mockCandidates: Candidate[] = [
      {
        id: "1",
        name: "Alex Johnson",
        title: "Senior Full Stack Developer",
        email: "alex@example.com",
        matchScore: 92,
        skills: [
          { name: "React", score: 95 },
          { name: "TypeScript", score: 90 },
          { name: "Node.js", score: 88 },
          { name: "AWS", score: 85 },
        ],
        avatar: "/placeholder.svg?height=40&width=40",
      },
      {
        id: "2",
        name: "Jamie Smith",
        title: "Full Stack Engineer",
        email: "jamie@example.com",
        matchScore: 87,
        skills: [
          { name: "React Native", score: 92 },
          { name: "TypeScript", score: 88 },
          { name: "GraphQL", score: 90 },
          { name: "MongoDB", score: 82 },
        ],
        avatar: "/placeholder.svg?height=40&width=40",
      },
      {
        id: "3",
        name: "Taylor Wilson",
        title: "Frontend Developer",
        email: "taylor@example.com",
        matchScore: 78,
        skills: [
          { name: "React", score: 90 },
          { name: "JavaScript", score: 85 },
          { name: "CSS", score: 92 },
          { name: "Figma", score: 80 },
        ],
        avatar: "/placeholder.svg?height=40&width=40",
      },
    ]

    setMatchResults(mockCandidates)
    setIsLoading(false)

    // Show toast notification after finding matches
    //if (!open && !enabled && mockCandidates.length > 0) {
    //  toast({
    //    title: "Candidate Matches Available!",
    //    description: `We found ${mockCandidates.length} candidates that match this job specification.`,
    //    action: (
    //      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
    //        View preview
    //      </Button>
    //    ),
    //  })
    //}
  }

  const matchCount = potentialMatchesCount?.totalProfiles??0

  //const getScoreColor = (score: number) => {
  //  if (score >= 90) return "bg-green-500"
  //  if (score >= 80) return "bg-emerald-500"
  //  if (score >= 70) return "bg-amber-500"
  //  return "bg-orange-500"
  //}

  const enableInstantMatch =async () => {
    try{
      await triggerInstantSearches({data:{hiringCampaignId}})
      setEnabled(true)
      setOpen(false)
    }catch(err){
      console.log(err)
    }



    //toast({
    //  title: "Instant Match Enabled!",
    //  description: "You'll now receive matches for this job automatically.",
    //})
  }

  useEffect(() => {
    // If we haven't checked for matches yet, do an initial check
    if (matchResults === null) {
      findMatches()
    }
  }, [matchResults])

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            disabled={!matchCount}
            onClick={() => {
              if(enabled){
                navigate({
                  from:'/',
                  to:'/allCampaigns/1234/instantmatch'
                })
              }
              if (!matchResults) {
                findMatches()
              }
              setOpen(true)
            }}
            className="relative group overflow-hidden px-6 py-3 h-auto gap-3 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-300 border-0"
          >
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <div>
                  <div className="font-semibold">Finding Matches...</div>
                  <div className="text-xs text-white/80">Scanning your candidate database</div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center bg-white/20 rounded-full p-2">
                  <ZapIcon className="h-5 w-5 text-white" />
                </div>

                <div className="flex flex-col items-start">
                  <div className="font-semibold flex items-center">
                    Instant Match
                    {enabled && (
                      <Badge variant="secondary" className="ml-2 bg-green-500/20 text-green-700 border-green-500/30">
                        Enabled
                      </Badge>
                    )}
                    {!enabled && matchResults && (
                      <Badge variant="secondary" className="ml-2 bg-white/20">
                        {matchCount}+ candidates
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-white/80">
                    {enabled
                      ? "Automatically matching candidates"
                      : matchResults
                        ? `Over ${matchCount} candidates match this job spec`
                        : "Find qualified candidates instantly"}
                  </div>
                </div>

                {!matchResults && (
                  <div className="absolute -right-1 -top-1">
                    <Badge className="bg-amber-500 text-white border-0 shadow-md animate-pulse">New</Badge>
                  </div>
                )}
              </>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[625px] bg-white p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ZapIcon className="h-5 w-5 text-indigo-500" />
              Enable Instant Match for {jobTitle}
              <MatchExplainer />
            </DialogTitle>
            <DialogDescription>
              {enabled
                ? "Instant Match is enabled. You'll automatically receive qualified candidates for this position."
                : "Preview potential matches from your candidate database and enable Instant Match to automatically find qualified candidates."}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500 mb-4" />
              <p className="text-sm text-muted-foreground">Analyzing your candidate database...</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Candidate Preview</h3>
                <p className="text-xs text-muted-foreground">{potentialMatchesCount?.totalProfiles} potential matches found</p>
              </div>

              {matchResults?.slice(0, 3).map((candidate) => (
                <Card key={candidate.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="w-12 h-12 border">
                        <AvatarImage src={candidate.avatar} alt={candidate.name} />
                        <AvatarFallback>
                          {candidate.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium truncate">{candidate.name}</h4>
                            <p className="text-sm text-muted-foreground truncate">{candidate.title}</p>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-xs text-muted-foreground">Match</span>
                            <span className="text-sm font-bold">{candidate.matchScore}%</span>
                          </div>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-1">
                          {candidate.skills.slice(0, 3).map((skill, index) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className={
                                requiredSkills.includes(skill.name)
                                  ? "border-green-500 text-green-700 bg-green-50"
                                  : niceToHaveSkills.includes(skill.name)
                                    ? "border-blue-500 text-blue-700 bg-blue-50"
                                    : "border-gray-200"
                              }
                            >
                              {skill.name}
                            </Badge>
                          ))}
                          {candidate.skills.length > 3 && (
                            <Badge variant="outline">+{candidate.skills.length - 3} more</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {matchResults && matchResults.length > 3 && (
                <div className="text-center text-sm text-muted-foreground">
                  + {matchResults.length - 3} more candidates match this job specification
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row gap-3 sm:gap-0 mt-6">
            {!enabled ? (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} className="sm:mr-auto">
                  Maybe later
                </Button>
                <Button onClick={enableInstantMatch} className="gap-2 bg-gradient-to-r from-violet-600 to-indigo-600">
                  <ZapIcon className="h-4 w-4" />
                  Enable Instant Match
                </Button>
              </>
            ) : (
              <Button onClick={() => setOpen(false)} className="sm:ml-auto">
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

